'use client';

import { useCallback, useEffect, useState } from 'react';
import { ErrorCode, FileRejection, useDropzone } from 'react-dropzone';
import { FieldValues, Path, useFormContext } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';

import { compressImage } from '../../lib/compressImage';

import { MAX_FILES, MAX_SIZE_BYTES } from './constants';
import { DZFile } from './types';

export const useImageUpload = <K extends FieldValues, T extends Path<K>>(
  disabled: boolean,
  fieldName: T
) => {
  const {
    setValue,
    watch,
    formState: { errors }
  } = useFormContext<K>();

  const [error, setError] = useState('');
  const files = watch(fieldName) as DZFile[];

  const onDrop = useCallback(
    async <U extends File>(acceptedFiles: U[], fileRejections: FileRejection[]) => {
      const updatedFiles: DZFile[] = [];

      try {
        for (const file of acceptedFiles) {
          const compressedImage = await compressImage(file);
          const timestamp = Date.now();
          const newFileName = `${timestamp}.webp`;

          const modifiedFile = new File([compressedImage], newFileName, {
            type: 'image/webp'
          }) as DZFile;

          modifiedFile.id = uuidv4();
          modifiedFile.preview = URL.createObjectURL(modifiedFile);
          modifiedFile.fileName = newFileName;

          updatedFiles.push(modifiedFile);
        }

        setValue(fieldName, [...files, ...updatedFiles] as any);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error compressing images:', error);
      }

      handleRejections(fileRejections);
    },
    [files, setValue, fieldName]
  );

  const handleRejections = (fileRejections: FileRejection[]) => {
    fileRejections.forEach((file) => {
      file.errors.forEach((err) => {
        if (err.code === ErrorCode.FileTooLarge) {
          setError(`Error: ${err.message}`);
        } else if (err.code === ErrorCode.FileInvalidType) {
          setError(`Error: ${err.message}`);
        } else if (err.code === ErrorCode.TooManyFiles) {
          setError(`Error: ${err.message}`);
        }
      });
    });
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: { 'image/*': [] },
    maxFiles: MAX_FILES,
    maxSize: MAX_SIZE_BYTES,
    onDrop,
    disabled
  });

  const deleteImage = useCallback(
    (id: string) => {
      setValue(fieldName, files.filter((file) => file.id !== id) as any);
    },
    [files, setValue, fieldName]
  );

  useEffect(() => {
    if (files?.length > MAX_FILES) {
      setError(
        `Вы пытаетесь загрузить свыше ${MAX_FILES} фото. Пожалуйста удалите ${files.length - MAX_FILES} фото.`
      );
    } else {
      setError('');
    }
  }, [files?.length]);

  useEffect(() => {
    return () => files?.forEach((file) => URL.revokeObjectURL(file.preview));
  }, [files]);

  return {
    getRootProps,
    getInputProps,
    files,
    error,
    deleteImage,
    errors
  };
};
